import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import Button from 'components/Buttons/Button';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeaderSubtitle';
import RadioButton from 'components/RadioButton';
import Shimmer from 'components/Shimmer';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { UpSellInclusion, UpSellInclusionType, UpSellOption } from 'models/upsell';
import PolicyTag from 'screens/components/UpsellOptions/components/PolicyTag';
import {
  getInclusions,
  findInclusionByCode,
  getType,
} from 'screens/components/UpsellOptions/components/UpsellDesktop/util';
import { getFormattedPrice } from 'utils/currency';
import { firstCapitalize } from 'utils/string';

import { Container, Table, YesIcon, NoIcon, Icon, Text, Price, ButtonContainer } from './styles';

interface Props {
  onClose: () => void;
  onSelect: (optionId: number) => void;
  onChange: (optionId: number) => void;
  options: UpSellOption[];
  loading: boolean;
}

const Item = ({ type, title }: { type: UpSellInclusionType; title?: string }) => {
  const { t } = useStoreTranslation();
  return type === UpSellInclusionType.AtCharge ? (
    <Fragment>
      <Text>{t('upsellFares.atCharge')}</Text>
    </Fragment>
  ) : type === UpSellInclusionType.Included ? (
    <Fragment>
      <Icon>
        <YesIcon />
      </Icon>
      {title && <Text>{firstCapitalize(title)}</Text>}
    </Fragment>
  ) : (
    <Icon>
      <NoIcon />
    </Icon>
  );
};

const UpSellDesktop = ({ onClose, onSelect, onChange, options, loading }: Props) => {
  const { t } = useStoreTranslation();
  const { currency } = useSelector((state: RootState) => state.i18n.country);
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };
  const [inclusions, setInclusions] = useState<UpSellInclusion[]>([]);
  const [selected, setSelected] = useState('');

  const handleChange = (value) => {
    setSelected(value);
    onChange(Number(value));
  };

  useEffect(() => {
    if (selected === '' && options.length > 0) setSelected(String(options[0].optionId));
  }, [selected, options]);

  useEffect(() => {
    if (options && options.length > 0) setInclusions(getInclusions(options));
  }, [options]);

  const getTitle = (option: UpSellOption, code: string): string => {
    const data = findInclusionByCode(option, code);
    return data?.type === UpSellInclusionType.Included
      ? data.title && data.title !== ''
        ? data.title
        : data.subTitle && data.subTitle !== ''
        ? data.subTitle
        : ''
      : '';
  };

  const renderCell = (o, idx, inc, iidx) => (
    <Table.Item key={`${idx}-${iidx}-${inc.code}`}>
      <Item type={getType(o, inc.code) as UpSellInclusionType} title={getTitle(o, inc.code)} />
    </Table.Item>
  );

  const renderColumn = (o, idx) => (
    <Table.Column key={idx}>
      <Table.Item key={`${idx}-title`} className="title">
        {o.families[0].name.toUpperCase()}
      </Table.Item>
      {inclusions.map((inc, iidx) => renderCell(o, idx, inc, iidx))}
      <Table.Item className="footer">
        <Table.Div>
          <PolicyTag isPreApproved={o.isPreApproved === true ? true : false} breakRules={o.breakRulesMessage || []} />
        </Table.Div>
        <Table.Div>
          <RadioButton value={String(o.optionId)} optionValue={selected} handleChangeValue={handleChange} />
          <Price>{formattedPrice(o.amount.fare + o.amount.taxes)}</Price>
        </Table.Div>
      </Table.Item>
    </Table.Column>
  );

  const renderCellSubTitle = (inc) => {
    const title = inc.title ? firstCapitalize(inc.title.replace('<br/>', '').replace('<br>', '')) : '';
    const subtitle = inc.subTitle ? firstCapitalize(inc.subTitle) : '';
    const label =
      title && subtitle ? `${title}. ${subtitle}` : title && !subtitle ? title : !title && subtitle ? subtitle : '';
    return label && <Table.ItemSmall>{label}</Table.ItemSmall>;
  };

  return (
    <Fragment>
      {loading ? (
        <Fragment>
          <ModalHeader
            onClose={onClose}
            title={<Shimmer>Customize your rate</Shimmer>}
            subtitle={<Shimmer>Select the rate that best suits you on your trip.</Shimmer>}
          />
          <Loader fullScreen={false} />
        </Fragment>
      ) : (
        options && (
          <Fragment>
            <ModalHeader onClose={onClose} title={t('upsellFares.title')} subtitle={t('upsellFares.subtitle')} />
            <Container>
              <Table>
                <Table.Container>
                  <Table.Column className="title">
                    <Table.Item className="title"></Table.Item>
                    {inclusions.map((inc, iidx) => (
                      <Table.Item className="head" key={`head-${iidx}`}>
                        {inc.rowName && firstCapitalize(inc.rowName)}
                        {renderCellSubTitle(inc)}
                      </Table.Item>
                    ))}
                    <Table.Item className="footer"></Table.Item>
                  </Table.Column>
                  {options.map((o, idx) => renderColumn(o, idx))}
                </Table.Container>
              </Table>

              <ButtonContainer>
                <Button onClick={() => onSelect(Number(selected))}>{t('upsellFares.button')}</Button>
              </ButtonContainer>
            </Container>
          </Fragment>
        )
      )}
    </Fragment>
  );
};
export default UpSellDesktop;
